import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { surfaceSx } from "../center/ownerCenterUi";

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function personLabel(person) {
  if (!person) return "";
  const company = String(person.company || "").trim();
  return company ? `${person.name} · ${company}` : String(person.name || "Persona");
}

function uniquePeople(rows = []) {
  const seen = new Set();
  return rows.filter((row) => {
    const id = idOf(row);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export default function OwnerProjectPeopleLinkPanel({ initiative, person, onChanged, onNavigate }) {
  const initiativeId = idOf(initiative);
  const currentId = idOf(person || initiative?.clientId || initiative?.client);
  const [people, setPeople] = React.useState([]);
  const [selected, setSelected] = React.useState(person || null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const loadPeople = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await ownerApi.listClients({ limit: 500 });
      const rows = uniquePeople([...(person ? [person] : []), ...(response?.clients || [])]);
      setPeople(rows);
      const linked = rows.find((row) => idOf(row) === currentId) || person || null;
      setSelected(linked);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar las personas de Owner."));
    } finally {
      setLoading(false);
    }
  }, [currentId, person]);

  React.useEffect(() => {
    loadPeople();
  }, [loadPeople]);

  React.useEffect(() => {
    if (person) setSelected(person);
    else if (!currentId) setSelected(null);
  }, [currentId, person]);

  const save = async (nextPerson) => {
    if (!initiativeId || saving) return;
    const nextId = idOf(nextPerson);
    if (nextId === currentId) return;
    setSaving(true);
    setError("");
    try {
      await ownerApi.updateInitiative(initiativeId, { clientId: nextId || null });
      setSelected(nextPerson || null);
      await onChanged?.();
    } catch (err) {
      setSelected(person || null);
      setError(formatApiError(err, "No pude vincular esta persona al proyecto."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-owner-jarvis-section="project-people" sx={{ ...surfaceSx, p: { xs: 1.5, md: 1.8 } }}>
      <Stack spacing={1}>
        <Box>
          <Typography sx={{ fontWeight: 1000 }}>Persona principal del proyecto</Typography>
          <Typography sx={{ mt: .18, color: "text.secondary", fontSize: ".72rem" }}>
            Esta relación pertenece a Owner. No requiere una cuenta, usuario ni Project de WebProfit.
          </Typography>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Stack direction="row" spacing={.8} alignItems="center" sx={{ py: .5 }}>
            <CircularProgress size={17} />
            <Typography sx={{ color: "text.secondary", fontSize: ".76rem" }}>Cargando personas…</Typography>
          </Stack>
        ) : (
          <Autocomplete
            fullWidth
            options={people}
            value={selected}
            disabled={saving}
            getOptionLabel={personLabel}
            isOptionEqualToValue={(option, value) => idOf(option) === idOf(value)}
            onChange={(_event, value) => save(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Vincular persona existente"
                placeholder="Buscar por nombre, empresa, mail…"
              />
            )}
            noOptionsText="No hay personas para mostrar"
          />
        )}

        {selected ? (
          <Box>
            <Typography sx={{ fontSize: ".8rem", fontWeight: 950 }}>{personLabel(selected)}</Typography>
            <Typography sx={{ mt: .14, color: "text.secondary", fontSize: ".7rem" }}>
              {[selected.email, selected.phone].filter(Boolean).join(" · ") || "Sin email ni teléfono cargado"}
            </Typography>
            <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap sx={{ mt: .7 }}>
              <Button
                size="small"
                startIcon={<OpenInNewRoundedIcon />}
                onClick={() => onNavigate?.(`/dashboard/owner/clientes?cliente=${encodeURIComponent(idOf(selected))}`)}
                sx={{ textTransform: "none", fontWeight: 900 }}
              >
                Abrir persona
              </Button>
              <Button
                size="small"
                color="inherit"
                startIcon={<LinkOffRoundedIcon />}
                disabled={saving}
                onClick={() => save(null)}
                sx={{ textTransform: "none", fontWeight: 850 }}
              >
                Desvincular
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Typography sx={{ color: "text.secondary", fontSize: ".76rem" }}>
              Este proyecto todavía no tiene una persona principal vinculada. Puede ser un freelance, proyecto interno o desarrollo a medida igualmente.
            </Typography>
            {!people.length ? (
              <Button
                size="small"
                startIcon={<PersonAddAlt1RoundedIcon />}
                onClick={() => onNavigate?.("/dashboard/owner/clientes")}
                sx={{ mt: .55, textTransform: "none", fontWeight: 900 }}
              >
                Ir a Personas
              </Button>
            ) : null}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
